import { getUserDisplayName } from "../lib/userDisplayName";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  Heart,
  MessageCircle,
  NotebookPen,
  ShieldOff,
  ArrowUpRight,
} from "lucide-react";
import {
  apiClient,
  type CommunityBlockedUser,
  type CommunityPostListItem,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { usePageSEO } from "../lib/usePageSEO";
import { mergeUniqueItems } from "../lib/communityBoardState";
import "../styles/community-renewal.css";

const tabs = [
  {
    id: "posts",
    name: "내 글",
    icon: NotebookPen,
    empty: "아직 작성한 글이 없어요",
    hint: "선생님의 첫 이야기를 들려주세요.",
  },
  {
    id: "likes",
    name: "좋아요",
    icon: Heart,
    empty: "좋아요한 글이 없어요",
    hint: "공감하는 글에 마음을 남겨보세요.",
  },
  {
    id: "commented",
    name: "댓글 단 글",
    icon: MessageCircle,
    empty: "아직 참여한 대화가 없어요",
    hint: "댓글을 남긴 이야기를 여기에서 다시 찾을 수 있어요.",
  },
  {
    id: "bookmarks",
    name: "북마크",
    icon: Bookmark,
    empty: "저장한 글이 없어요",
    hint: "다시 읽고 싶은 글을 북마크해 보세요.",
  },
  {
    id: "blocks",
    name: "차단 관리",
    icon: ShieldOff,
    empty: "차단한 사용자가 없어요",
    hint: "차단한 사용자는 여기에서 해제할 수 있어요.",
  },
] as const;
type Tab = (typeof tabs)[number]["id"];

export function CommunityMyActivity() {
  const { isAuthenticated } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = (tabs.find((t) => t.id === params.get("tab"))?.id ||
    "posts") as Tab;
  const currentTab = tabs.find((t) => t.id === tab)!;
  const [items, setItems] = React.useState<CommunityPostListItem[]>([]);
  const [blocked, setBlocked] = React.useState<CommunityBlockedUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [hasNext, setHasNext] = React.useState(false);
  const [unblocking, setUnblocking] = React.useState<number | null>(null);
  const requestVersion = React.useRef(0);
  const loadPending = React.useRef(false);
  const unblockPending = React.useRef(false);

  usePageSEO({
    title: "내 활동 - Foreducator 소통공간",
    description:
      "내가 쓴 글, 좋아요, 댓글 단 글, 북마크, 차단 관리를 한곳에서 확인하세요.",
    path: "/community/my",
    keywords: "내 글, 좋아요, 북마크, Foreducator 소통공간",
  });
  const load = React.useCallback(
    async (nextPage: number, append = false) => {
      if (!isAuthenticated || (append && loadPending.current)) return;
      const version = ++requestVersion.current;
      loadPending.current = true;
      setLoading(true);
      setError(null);
      if (!append) {
        setItems([]);
        setBlocked([]);
        setHasNext(false);
      }
      try {
        if (tab === "blocks") {
          const result = await apiClient.listCommunityBlocks();
          if (version === requestVersion.current) {
            setBlocked(result.results || []);
            setHasNext(false);
            setPage(1);
          }
        } else {
          const options = { page: nextPage, limit: 20 };
          const result = await (tab === "likes"
            ? apiClient.listMyCommunityLikes(options)
            : tab === "commented"
              ? apiClient.listMyCommunityCommented(options)
              : tab === "bookmarks"
                ? apiClient.listMyCommunityBookmarks(options)
                : apiClient.listMyCommunityPosts(options));
          if (version !== requestVersion.current) return;
          setItems((previous) =>
            mergeUniqueItems(append ? previous : [], result.results, (p) =>
              String(p.id),
            ),
          );
          setHasNext(Boolean(result.next));
          setPage(nextPage);
        }
      } catch {
        if (version === requestVersion.current)
          setError("활동을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.");
      } finally {
        if (version === requestVersion.current) {
          setLoading(false);
          loadPending.current = false;
        }
      }
    },
    [tab, isAuthenticated],
  );
  React.useEffect(() => {
    void load(1);
    return () => {
      requestVersion.current += 1;
      loadPending.current = false;
    };
  }, [load]);
  const unblock = async (id: number) => {
    if (unblockPending.current) return;
    unblockPending.current = true;
    setUnblocking(id);
    setError(null);
    try {
      await apiClient.toggleCommunityBlock(id);
      setBlocked((previous) => previous.filter((user) => user.id !== id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "차단을 해제하지 못했어요.");
    } finally {
      unblockPending.current = false;
      setUnblocking(null);
    }
  };
  const EmptyIcon = currentTab.icon;
  return (
    <div className="fe-page-canvas fe-community">
      <div className="fe-community-layout">
        <Link to="/community/board" className="fe-community-back">
          <ArrowLeft size={16} />
          소통공간으로
        </Link>
        <header className="fe-community-page-heading">
          <div className="fe-community-heading-icon">
            <NotebookPen size={27} />
          </div>
          <div>
            <p className="fe-eyebrow">MY COMMUNITY</p>
            <h1>
              내 활동<span className="fe-heading-dot">.</span>
            </h1>
            <p>내가 나눈 이야기와 다시 보고 싶은 글을 한곳에서.</p>
          </div>
        </header>
        {!isAuthenticated ? (
          <div className="fe-community-empty">
            <h2>나의 이야기를 모아 보세요</h2>
            <p>로그인하면 내 글, 좋아요, 북마크를 확인할 수 있어요.</p>
            <Link
              to="/login?next=%2Fcommunity%2Fmy"
              className="fe-button fe-button-dark"
            >
              로그인
            </Link>
          </div>
        ) : (
          <>
            <div className="fe-community-filters">
              <div
                className="fe-community-tabs"
                role="group"
                aria-label="활동 종류"
              >
                {tabs.map(({ id, name, icon: Icon }) => (
                  <button
                    key={id}
                    aria-pressed={tab === id}
                    onClick={() => setParams({ tab: id })}
                  >
                    <Icon size={15} />
                    {name}
                  </button>
                ))}
              </div>
            </div>
            {error && (
              <div className="fe-community-error" role="alert">
                <p>{error}</p>
                <button
                  className="fe-community-button"
                  onClick={() =>
                    void load(
                      items.length && hasNext ? page + 1 : 1,
                      Boolean(items.length && hasNext),
                    )
                  }
                >
                  다시 불러오기
                </button>
              </div>
            )}
            <section
              className="fe-community-feed"
              aria-label={currentTab.name}
              aria-busy={loading}
            >
              {loading && !items.length && !blocked.length ? (
                <div
                  className="fe-community-skeleton"
                  role="status"
                  aria-label="활동 불러오는 중"
                >
                  {Array.from({ length: 4 }, (_, i) => (
                    <div key={i}>
                      <span />
                      <span />
                    </div>
                  ))}
                </div>
              ) : tab === "blocks" && blocked.length ? (
                <ul className="fe-community-stories">
                  {blocked.map((user) => (
                    <li key={user.id} className="fe-community-block-row">
                      <div>
                        <strong>{getUserDisplayName(user)}</strong>
                        <p>
                          차단일{" "}
                          {user.blocked_at
                            ? new Date(user.blocked_at).toLocaleDateString(
                                "ko-KR",
                              )
                            : "—"}
                        </p>
                      </div>
                      <button
                        disabled={unblocking !== null}
                        onClick={() => void unblock(user.id)}
                        className="fe-community-button"
                      >
                        {unblocking === user.id ? "해제 중…" : "차단 해제"}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : tab !== "blocks" && items.length ? (
                <ul className="fe-community-stories">
                  {items.map((post) => (
                    <li key={post.id} className="fe-community-row">
                      <Link
                        to={`/community/board/${post.id}`}
                        className="fe-community-row-link"
                      >
                        <div className="fe-community-row-main">
                          {post.subcategory?.name && (
                            <span className="fe-community-badge">
                              {post.subcategory.name}
                            </span>
                          )}
                          <span className="fe-community-row-title">
                            {post.title}
                          </span>
                        </div>
                        <div className="fe-community-row-meta">
                          <time dateTime={post.created_at}>
                            {new Date(post.created_at).toLocaleDateString(
                              "ko-KR",
                            )}
                          </time>
                          <span className="fe-community-metric">
                            <Heart size={14} />
                            <span className="sr-only">좋아요 </span>
                            {post.num_likes}
                          </span>
                          <span className="fe-community-metric">
                            <MessageCircle size={14} />
                            <span className="sr-only">댓글 </span>
                            {post.num_comments}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : !error ? (
                <div className="fe-community-empty">
                  <div className="fe-community-empty-icon">
                    <EmptyIcon size={28} />
                  </div>
                  <h2>{currentTab.empty}</h2>
                  <p>{currentTab.hint}</p>
                  <Link
                    to={
                      tab === "posts"
                        ? "/community/board/new"
                        : "/community/board"
                    }
                    className="fe-community-button"
                  >
                    {tab === "posts" ? "이야기 쓰기" : "이야기 둘러보기"}
                    <ArrowUpRight size={15} />
                  </Link>
                </div>
              ) : null}
              {hasNext && (
                <div className="fe-community-pagination">
                  <button
                    disabled={loading}
                    onClick={() => void load(page + 1, true)}
                    className="fe-community-button"
                  >
                    {loading ? "불러오는 중…" : "활동 더 보기"}
                  </button>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
